import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * EL COMERCIO QUE SE ESTÁ MIRANDO.
 *
 * 🔴 Existe para que el filtro por comercio NO se escriba en 56 pantallas.
 *
 * Se midió: 56 pantallas de la consola consumen datos que ahora llevan comercio,
 * a través de 142 métodos del servicio. Escribir el filtro en cada una
 * garantiza que en un año esté puesto de cuatro formas y falte en algunas —
 * que es la enfermedad que este sistema ya tiene con la clasificación de
 * producto: 692 líneas, cuatro dialectos, y cuatro que fallan en silencio.
 *
 * Por eso: acá se guarda cuál es, y un interceptor lo agrega a cada petición.
 * Ninguna pantalla se entera.
 *
 * 📍 Se guarda el CÓDIGO, no el identificador interno: `?merchant=EPA` se lee en
 * la URL y en un log, y el back resuelve las dos formas.
 */
@Injectable({ providedIn: 'root' })
export class ComercioActivoService {

  /** La clave del navegador. Con prefijo para no chocar con lo que ya hay. */
  private static readonly CLAVE = 'mc_comercio_activo';

  /**
   * 🔴 `null` significa TODOS LOS COMERCIOS — la vista de CrediTotal — y NO es
   * lo mismo que MundoTotal. Confundirlos da números mal: lo que no tiene marca
   * es de MundoTotal, no es "todo".
   */
  private readonly _codigo = new BehaviorSubject<string | null>(null);

  /** Para que la barra superior y quien quiera se enteren del cambio. */
  readonly codigo$ = this._codigo.asObservable();

  constructor() {
    // Sobrevive a recargar la página: si no, se pierde en cada F5 y el operador
    // termina mirando otro comercio sin darse cuenta.
    try {
      const guardado = localStorage.getItem(ComercioActivoService.CLAVE);
      if (guardado) this._codigo.next(guardado);
    } catch { /* navegador sin almacenamiento: se arranca en "todos" */ }
  }

  get codigo(): string | null { return this._codigo.getValue(); }

  get esTodos(): boolean { return this._codigo.getValue() === null; }

  /** `null` vuelve a la vista de todos los comercios. */
  seleccionar(codigo: string | null): void {
    const limpio = codigo ? codigo.trim().toUpperCase() : null;
    if (limpio === this._codigo.getValue()) return;
    try {
      if (limpio) localStorage.setItem(ComercioActivoService.CLAVE, limpio);
      else localStorage.removeItem(ComercioActivoService.CLAVE);
    } catch { /* sin almacenamiento: vale solo para esta sesión */ }
    this._codigo.next(limpio);
  }

  limpiar(): void { this.seleccionar(null); }

  /**
   * LAS PANTALLAS DONDE EL SELECTOR APLICA. En el resto ni se ve ni viaja.
   *
   * 🔴 Es una lista de las que SÍ, no de las que no. Una pantalla nueva arranca
   * sin filtro hasta que alguien decida que lo necesita: lo contrario ya pasó —
   * el comercio viajaba a todas, y Planes quedaba vacía, y los combos de tiendas
   * de Roles y Usuarios mostraban solo las del comercio elegido.
   *
   * Criterio (Reybier, 6-oct-2026): se separa lo que es del comercio — sus
   * ventas, sus cuotas, sus tiendas, sus solicitudes. El CLIENTE no es de un
   * comercio, es de CrediTotal: Contactos, la ficha del cliente, su cupo y las
   * consultas de clientes no llevan selector.
   *
   * Y solo entran las pantallas que DE VERDAD filtran: un selector que se ve y
   * no cambia nada es peor que no tenerlo. Pendientes, y por qué no están:
   *   - /console/cartera  → lee de invoice_views, que hoy no guarda el comercio
   *   - pagos (Verificación de Pagos, Compras / Pagos) → SIN selector por decisión
   *     de Juan (reunión del 6-oct): un pago puede repartirse entre comercios.
   *     Esas pantallas siguen mostrando la columna «Comercio» con el desglose.
   *   - /console/caja     → habla con payment-pos, otro servicio
   *   - domiciliación     → falta decidir si el lote se separa por comercio
   *
   * Se compara por PREFIJO de la ruta.
   */
  private static readonly RUTAS_CON_SELECTOR: readonly string[] = [
    // Solicitudes de crédito
    '/console/solicitudes-credito',
    // Ventas: Compras Financiadas y su detalle por estado y tienda
    '/console/invoice-dashboard',
    '/console/financial-payments',
    // Cobranza: Gestión de Cuotas
    '/console/installments-console',
    // Productos de Compra
    '/console/catalog-products',
    // Tiendas (Parámetros de Sistema)
    '/console/shops',
    // Reportería NO va: por pedido de Ángela (2026-10-09) solo se filtra la Cartera de
    // Crédito, con su propio campo «Comercio» dentro del formulario. Si Reportería
    // dependiera de este selector, todos sus reportes recibirían un comercio y la
    // mayoría lo ignora sin avisar.
  ];

  /**
   * La ruta a la que se está entrando. La marca la barra superior en cada
   * NavigationStart: el navegador cambia la dirección recién al final de la
   * navegación, y la pantalla nueva ya pide sus datos antes.
   */
  private _rutaEnCurso: string | null = null;

  marcarRuta(ruta: string): void { this._rutaEnCurso = ruta; }

  /** La ruta que vale para decidir: la que está entrando, o la del navegador. */
  get rutaActual(): string {
    return this._rutaEnCurso || window.location.pathname;
  }

  /** Rutas que aplican SOLO exactas: su prefijo también cubre pantallas que no. */
  // Hoy vacía: Verificación de Pagos y Compras / Pagos salieron del selector
  // (ver arriba). Se deja el mecanismo porque '/console/payments' a secas es
  // también el prefijo de los pagos de UN cliente.
  private static readonly RUTAS_EXACTAS_CON_SELECTOR: readonly string[] = [];

  /** ¿El selector aplica en esta ruta? */
  aplicaEn(ruta: string): boolean {
    const limpia = (ruta || '').split('?')[0].split('#')[0].replace(/\/+$/, '');
    return ComercioActivoService.RUTAS_EXACTAS_CON_SELECTOR.includes(limpia)
      || ComercioActivoService.RUTAS_CON_SELECTOR.some((p) => limpia.startsWith(p));
  }

  /** El comercio que vale en esta ruta: el elegido si aplica, si no ninguno. */
  codigoPara(ruta: string): string | null {
    return this.aplicaEn(ruta) ? this.codigo : null;
  }
}
